<template>
    <ChatTeamCreate
        v-if="result?.status === 'no_teams' && result.create_form_url && !selectedTeam"
        :form-url="result.create_form_url"
        :picker-url="result.picker_url"
        :selection-error="error"
        :disabled="busy || selectionPending"
        @selected="onTeamSelected"
        @available="refreshedCard = $event" />
    <BaseCard v-else-if="result?.status === 'no_teams' && !selectedTeam" theme="cream" radius="lgr" padding="sm" class="flex w-full min-w-0 max-w-150 flex-wrap items-center gap-3">
        <BaseAvatar shape="square" size="md"><i class="bi bi-people-fill" aria-hidden="true"></i></BaseAvatar>
        <h3 class="nadia-chat-bold min-w-0 flex-1 text-ink">{{ t("teamPerspective.title") }}</h3>
        <BaseLink v-if="result.action_url" :href="createPageUrl" class="base-btn-neutral base-btn-sm base-btn-with-icon base-btn-icon-start shrink-0">
            <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
            {{ t("chat.teamPerspective.createTeam") }}
        </BaseLink>
    </BaseCard>
    <BaseCard v-else-if="result?.show_card !== false" theme="cream" radius="lgr" :padding="selectedTeam ? 'md' : 'sm'" class="w-full min-w-0 max-w-150">
        <div class="flex items-center gap-3 mb-3">
            <h3 ref="headingEl" tabindex="-1" class="nadia-chat-bold min-w-0 flex-1 break-words text-ink" :class="{ 'text-center': selectedTeam }">
                {{ heading }}
            </h3>
            <BaseDrawer
                :open="createOpen"
                :title="t('chat.teamPerspective.createTeam')"
                title-voice="serif"
                mobile-layout="fullscreen"
                :reserve-mobile-nav-space="false"
                :swipe-to-close="false"
                :dismissible="!createSubmitting && !selectionPending"
                :return-focus-to="selectedTeam ? headingEl : null"
                @update:open="setCreateOpen">
                <template v-if="canCreate" #trigger>
                    <BaseButton theme="neutral" size="sm" class="base-btn-with-icon base-btn-icon-start shrink-0" :disabled="selectionPending">
                        <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
                        {{ t("chat.teamPerspective.createTeam") }}
                    </BaseButton>
                </template>
                <ChatTeamCreate
                    v-if="result?.create_form_url"
                    embedded
                    class="p-5 sm:p-6"
                    :form-url="result.create_form_url"
                    :selection-error="error"
                    :disabled="busy || selectionPending"
                    @submitting="createSubmitting = $event"
                    @selected="onTeamSelected" />
            </BaseDrawer>
        </div>
        <ChatTeamPerspectiveResults
            v-if="hasSavedSelection && selectedTeam && selectedReportUrl"
            :key="selectedTeam.id"
            :url="selectedReportUrl"
            :focus="result?.preview"
            preview />
        <BaseDrawer
            v-if="hasSavedSelection && selectedTeam && selectedReportUrl"
            :key="selectedTeam.id"
            :title="selectedTeam.name"
            title-voice="serif"
            mobile-layout="fullscreen"
            :reserve-mobile-nav-space="false">
            <template #trigger>
                <BaseButton theme="neutral" size="sm" class="mt-4 w-full">{{ t("chat.collaboration.seeMore") }}</BaseButton>
            </template>
            <ChatTeamPerspectiveResults :url="selectedReportUrl" />
        </BaseDrawer>
        <template v-if="!selectedTeam && teams.length">
            <ul class="-mx-4 divide-y divide-border border-t border-border px-4" :aria-label="t('chat.teamPerspective.teamsLabel')">
                <li v-for="team in teams" :key="team.id" class="flex items-center justify-between gap-4 py-3 last:pb-0">
                    <div class="flex min-w-0 items-center gap-3">
                        <BaseAvatar shape="square" size="md" class="shrink-0"><i class="bi bi-people-fill" aria-hidden="true"></i></BaseAvatar>
                        <div class="min-w-0 break-words text-ink">
                            <span class="body-small block">{{ team.name }}</span>
                            <p v-if="team.member_names?.length" :id="`${pickerId}-${team.id}`" class="scaffold-details-small text-ink-muted">
                                {{ t("chat.teamPerspective.withMembers", { names: team.member_names.join(", ") }) }}
                            </p>
                        </div>
                    </div>
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        class="shrink-0"
                        :disabled="busy || selectionPending"
                        :aria-label="t('chat.teamPerspective.chooseTeam', { name: team.name })"
                        :aria-describedby="team.member_names?.length ? `${pickerId}-${team.id}` : undefined"
                        @click="selectTeam(team)">
                        {{ t("chat.teamPerspective.choose") }}
                    </BaseButton>
                </li>
            </ul>
            <BaseButton v-if="nextUrl" theme="tertiary" size="sm" class="mt-3" :disabled="loading" @click="loadMore">
                {{ loading ? t("chat.teamPerspective.loading") : t("chat.teamPerspective.moreTeams") }}
            </BaseButton>
        </template>
        <p v-if="error && !createOpen" class="body-small text-ink mt-3" role="alert">{{ error }}</p>
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { TeamPerspectiveChatCard, TeamPerspectiveSummary } from "~vue/api/generated/team-perspective";
import { ACTION } from "~vue/chatActions";
import ChatTeamCreate from "~vue/ChatDetail/ChatTeamCreate.vue";
import ChatTeamPerspectiveResults from "~vue/ChatDetail/ChatTeamPerspectiveResults.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDrawer from "~vue/components/BaseDrawer.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import type { ChatMessageLine } from "~vue/types/chat";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { Ref } from "vue";
import { computed, inject, ref, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({ inheritAttrs: false });
const props = defineProps<{ action?: ChatMessageLine | null }>();
const { t } = useI18n();
const pickerId = useId();
const busy = inject<Ref<boolean>>("teamPerspectiveBusy", ref(false));
const savedCard = computed(() => (props.action?.type === "action" && props.action.action_name === ACTION.GET_TEAM_PERSPECTIVE ? props.action.action_params : null));
const refreshedCard = ref<TeamPerspectiveChatCard | null>(null);
const result = computed(() => refreshedCard.value ?? savedCard.value);
watch(savedCard, () => {
    refreshedCard.value = null;
});
const createPageUrl = computed(() => {
    if (!result.value?.action_url) {
        return "";
    }
    const url = new URL(result.value.action_url, globalThis.location.origin);
    url.searchParams.set("team_source", TEAM_INTERACTION_SOURCE.CHAT_TOOL);
    return `${url.pathname}${url.search}${url.hash}`;
});
const teams = ref<TeamPerspectiveSummary[]>([]);
const nextUrl = ref<string | null>(null);
const selectedTeam = ref<TeamPerspectiveSummary | null>(null);
const selectionPending = ref(false);
const createOpen = ref(false);
const createSubmitting = ref(false);
const loading = ref(false);
const error = ref("");
const sendMessage = inject<(content: string, options: { teamPerspectiveTeamId: string; teamPerspectiveToolCallId?: string }) => boolean>("sendMessage", () => false);
watch(
    result,
    (value) => {
        teams.value = [...(value?.teams ?? [])];
        nextUrl.value = value?.next_url ?? null;
        selectedTeam.value = value?.selected_team ?? null;
        if (selectedTeam.value) {
            selectionPending.value = false;
            error.value = "";
            createOpen.value = false;
        }
    },
    { immediate: true },
);

const selectedReportUrl = computed(() =>
    selectedTeam.value && (result.value?.status === "found" || result.value?.status === "no_results") ? `/v1/teams/${selectedTeam.value.id}/report` : "",
);
const hasSavedSelection = computed(() => Boolean(selectedTeam.value && selectedTeam.value.id === result.value?.selected_team?.id));
const headingEl = useTemplateRef<HTMLElement>("headingEl");
const canCreate = computed(() => !selectedTeam.value && result.value?.status === "available" && Boolean(result.value.create_form_url));
const heading = computed(() => selectedTeam.value?.name ?? t("chat.teamPerspective.chooseTitle"));

watch(busy, (waiting) => {
    if (!waiting && selectionPending.value) {
        selectionPending.value = false;
        if (!hasSavedSelection.value) {
            error.value = t("chat.teamPerspective.sendError");
        }
    }
});

function selectTeam(team: TeamPerspectiveSummary, explicitSelection = true) {
    if (selectedTeam.value || busy.value || selectionPending.value) {
        return;
    }
    error.value = "";
    if (
        sendMessage(t("chat.teamPerspective.selectionMessage", { team_name: team.name }), {
            teamPerspectiveTeamId: team.id,
            teamPerspectiveToolCallId: savedCard.value?.tool_call_id,
        })
    ) {
        selectionPending.value = true;
        if (explicitSelection) {
            logUserInteraction(AmplitudeEventNames.TEAM_SELECTED, { source: TEAM_INTERACTION_SOURCE.CHAT_TOOL, team_id: team.id });
        }
    } else {
        error.value = t("chat.teamPerspective.sendError");
    }
}

function setCreateOpen(open: boolean) {
    if (createSubmitting.value || selectionPending.value) {
        return;
    }
    createOpen.value = open;
    error.value = "";
}

function onTeamSelected(team: TeamPerspectiveSummary) {
    if (!teams.value.some((existing) => existing.id === team.id)) {
        teams.value.unshift(team);
    }
    selectTeam(team, false);
}

async function loadMore() {
    if (!nextUrl.value || loading.value) {
        return;
    }
    loading.value = true;
    logUserInteraction(AmplitudeEventNames.TEAMS_MORE_CLICKED, { source: TEAM_INTERACTION_SOURCE.CHAT_TOOL });
    error.value = "";
    try {
        const { data, error: failure } = await useFetch(nextUrl.value).get().json<TeamPerspectiveChatCard>();
        if (failure.value || !data.value) {
            error.value = t("chat.teamPerspective.loadError");
            return;
        }
        const existing = new Set(teams.value.map((team) => team.id));
        teams.value.push(...(data.value.teams ?? []).filter((team) => !existing.has(team.id)));
        nextUrl.value = data.value.next_url ?? null;
    } catch {
        error.value = t("chat.teamPerspective.loadError");
    } finally {
        loading.value = false;
    }
}
</script>
